/**
 * Create a Data Room document: title, category, and Markdown content with
 * the same edit-and-preview editor tasks use.
 *
 * The page is location-aware: arriving from inside a folder
 * (`?folder=<id>`) fixes the destination — the server derives the shelf
 * from the folder — and arriving from a category root (`?category=…`)
 * preselects the shelf. Arriving cold offers the six shelves.
 *
 * Success navigates straight to the new document — the Data Room's job is
 * reading, and the freshly created page is the proof the save happened.
 * One idempotency key per creation intent, reused across retries of it.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  DATA_ROOM_CATEGORIES,
  DATA_ROOM_MAX_CONTENT_LENGTH,
  DATA_ROOM_MAX_TITLE_LENGTH,
  isDataRoomCategory,
} from '@mouja/domain/data-room';

import { ApiFailure, createDataRoomDocument, newIdempotencyKey } from '../api.js';
import { MarkdownField, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import { workspaceSlots } from '../slots.js';
import { Button, ChoiceGroup, InlineAlert, TextField, useToasts } from '../ui.js';
import { PageTitle } from './common.js';
import { NotAvailablePage } from './NotAvailablePage.js';

export function DataRoomNewPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const { announce } = useToasts();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const folderId = searchParams.get('folder');
  const presetCategory = searchParams.get('category');

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string | null>(
    presetCategory !== null && isDataRoomCategory(presetCategory)
      ? presetCategory
      : null,
  );
  const [content, setContent] = useState('');
  const [audience, setAudience] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [intentKey] = useState(() => newIdempotencyKey());

  const errors = useMemo(() => {
    const found: { field: string; message: string }[] = [];
    if (title.trim().length === 0) {
      found.push({ field: 'title', message: copy.dataRoom.titleMissing });
    }
    if (content.trim().length === 0) {
      found.push({ field: 'content', message: copy.dataRoom.contentMissing });
    }
    return found;
  }, [title, content, copy]);

  const submit = useCallback(() => {
    setSubmitted(true);
    setFailure(null);
    if (errors.length > 0 || (folderId === null && category === null)) return;
    setBusy(true);
    createDataRoomDocument(intentKey, {
      title: title.trim(),
      // Inside a folder the destination is fixed; the server derives the
      // shelf from the folder, so a stale category cannot disagree with it.
      ...(folderId === null ? { category: category ?? '' } : { folderId }),
      ...(audience === null ? {} : { audience }),
      content,
    })
      .then((result) => {
        announce(copy.dataRoom.toastCreated);
        void navigate(`/data-room/${result.documentId}`, { replace: true });
      })
      .catch((thrown: unknown) => {
        setBusy(false);
        setFailure(thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'));
      });
  }, [
    errors,
    folderId,
    category,
    audience,
    title,
    content,
    intentKey,
    announce,
    copy,
    navigate,
  ]);

  if (session.status === 'authenticated' && !session.profile.mayUseDataRoom) {
    return <NotAvailablePage />;
  }

  return (
    <>
      <PageTitle>{copy.dataRoom.createTitle}</PageTitle>

      {submitted && (errors.length > 0 || (folderId === null && category === null)) ? (
        <InlineAlert tone="error" title={copy.dataRoom.errorSummary} role="alert">
          <ul style={{ margin: 0, paddingInlineStart: 20 }}>
            {errors.map((error) => (
              <li key={error.field}>{error.message}</li>
            ))}
          </ul>
        </InlineAlert>
      ) : null}

      {failure !== null ? (
        <InlineAlert
          tone={failure.kind === 'offline' ? 'warning' : 'error'}
          title={
            failure.kind === 'offline'
              ? copy.states.offlineTitle
              : copy.states.saveFailedTitle
          }
          role="alert"
        >
          {failure.kind === 'offline'
            ? copy.states.offlineBody
            : (failure.detail ?? copy.states.saveFailedBody)}
        </InlineAlert>
      ) : null}

      <form
        style={{ maxWidth: 720 }}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={copy.dataRoom.titleLabel}
          hint={copy.dataRoom.titleHint}
          value={title}
          onChange={setTitle}
          maxLength={DATA_ROOM_MAX_TITLE_LENGTH}
          error={
            submitted && title.trim().length === 0 ? copy.dataRoom.titleMissing : null
          }
        />

        {folderId === null ? (
          <ChoiceGroup
            legend={copy.dataRoom.categoryLabel}
            value={category}
            onChange={setCategory}
            choices={DATA_ROOM_CATEGORIES.map((value) => ({
              value,
              label: copy.dataRoom.categories[value] ?? value,
              definition: copy.dataRoom.categoryDefinitions[value],
            }))}
          />
        ) : null}

        {workspaceSlots().documentCreateFields?.({
          value: audience,
          onChange: setAudience,
        })}

        <MarkdownField
          label={copy.dataRoom.contentLabel}
          hint={copy.dataRoom.contentHint}
          value={content}
          onChange={setContent}
          rows={16}
          maxLength={DATA_ROOM_MAX_CONTENT_LENGTH}
          error={
            submitted && content.trim().length === 0
              ? copy.dataRoom.contentMissing
              : null
          }
        />

        <Button variant="primary" type="submit" busy={busy}>
          {busy ? copy.dataRoom.creating : copy.dataRoom.create}
        </Button>
      </form>
    </>
  );
}
